import React, { useState, useEffect, Fragment, useCallback, useRef, useContext } from 'react';
import { Button, OverlayTrigger, Tooltip, Modal, Form, Row, Col, Spinner } from 'react-bootstrap';
import { getProjectSubtasks, getProjectTaskInvoices, createSubtasks, editSubtask, deleteSubtask } from '@lib/api';
import { IcoEdit, IcoMinusCircled, IcoPlus, IcoLink } from '@icons';
import Select from 'react-select';
import StatusBar from '@components/StatusBar';
import { translate } from 'components/translation/translation';
import { OETrans } from 'components/translation/OETrans';
import { dateFormatter, dateFormatterInput, formatToDecimals } from 'lib/helpers/formatting';
import { columnCentered } from 'lib/helpers/agGridHelpers';
import ProjectInvoiceModal from 'components/invoice/projectInvoiceModal';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import '@ag-grid-community/core/dist/styles/ag-grid.css';
import '@ag-grid-community/core/dist/styles/ag-theme-alpine.css';
import AG_GRID_LOCALE_IT from '@components/ag-grid/locale-it';
import CustomPaginationMui from 'components/materialComponents/CustomPaginationMui';
import InvoiceDetailSummaryModal from '@components/summary/InvoiceDetailSummaryModal';
import { UserContext } from 'components/contexts/UserProvider';

const ActionBtnRendererTasks = ({
	data,
	setRefresh,
	projectId,
	canEdit,
	optionsStatus,
	streams,
	projectStreamConfigurationType,
	projectAdvancedConfig,
	isCoordinator,
}) => {
	const { userAccess } = useContext(UserContext);
	const taskId = data?.id;
	const projectWpId = data?.projectStreamId;
	const wpId = data?.streamId;
	const wpStartDate = data?.startDate;
	const wpEndDate = data?.endDate;
	const [subtasks, setSubtasks] = useState([]);
	const [originSubtasks, setOriginSubtasks] = useState([]);
	const [taskInvoices, setTaskInvoices] = useState(null);
	const [taskInvoicesMeta, setTaskInvoicesMeta] = useState({});
	const [showModalEdit, setShowModalEdit] = useState(false);
	const [showModalLink, setShowModalLink] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [sbShow, setSbShow] = useState(<></>);
	const [show, setShow] = useState(false);
	const gridRef = useRef();
	//console.log('taskInvoices', taskInvoices);

	const fetchSubtasks = useCallback(
		async (afterSaving = false) => {
			if (showModalEdit && projectId && taskId && projectWpId) {
				getProjectSubtasks(projectId, projectWpId, taskId, true).then((retrievedSubtasks) => {
					if (retrievedSubtasks.success) {
						setSubtasks(retrievedSubtasks.data);
						setOriginSubtasks(retrievedSubtasks.data);
					}
				});
			}
		},
		[showModalEdit, projectId, taskId, projectWpId],
	);

	const fetchTaskInvoices = useCallback(async () => {
		if (showModalLink && projectId && taskId && projectWpId) {
			getProjectTaskInvoices(projectId, projectWpId, taskId).then((retrievedInvoices) => {
				if (retrievedInvoices.success) {
					setTaskInvoices(retrievedInvoices.data);

					const { data, success, ...restResp } = retrievedInvoices;
					setTaskInvoicesMeta(restResp);
				}
			});
		}
	}, [showModalLink, projectId, taskId, projectWpId]);

	const makeSemaphores = (data) => {
		//console.log('data', data);
		const stoplightColor = {
			red: '#C94723',
			yellow: '#D4BE31',
			green: '#689689',
			white: '#ffffff',
			gray: '#C4C4C4',
			grey: '#C4C4C4',
			green_white: '#689689',
		};

		let circleStyle;
		let message = '';

		if (data) {
			if (data.color === 'green_white' || data.color === 'green_white_settled') {
				circleStyle = {
					display: 'inherit',
					width: '23px',
					height: '23px',
					border: '4px solid #689689',
					borderRadius: '50%',
					padding: '0px',
					background: data.color === 'green_white_settled' ? 'linear-gradient(to right, #689689 50%, transparent 0)' : 'white',
					boxSizing: 'border-box',
				};

				message = data.color === 'green_white_settled' ? 'Conforme - validata' : 'Conforme - da validare';
			} else {
				circleStyle = {
					display: 'inherit',
					width: '1.2em',
					height: '1.2em',
					borderRadius: '2em',
					MozBorderRadius: '2em',
					WebkitBorderRadius: '2em',
					backgroundColor: stoplightColor[data.color],
					border: `2px solid ${stoplightColor[data.color]}`,
				};

				switch (data.color) {
					case 'white':
						message = 'Da lavorare';
						break;
					case 'grey':
					case 'gray':
						message = 'In lavorazione';
						break;
					case 'red':
						message = 'Non conforme';
						break;
					case 'yellow':
						message = 'Incompleta';
						break;
					case 'green':
						message = 'Conforme';
						break;
					default:
						break;
				}
			}
		} else {
			circleStyle = {
				display: 'inherit',
				width: '1.2em',
				height: '1.2em',
				borderRadius: '2em',
				MozBorderRadius: '2em',
				WebkitBorderRadius: '2em',
				backgroundColor: '#ffffff',
				border: `2px solid ${'#C4C4C4'}`,
			};

			message = 'Da lavorare';
		}

		return (
			<Row className="align-items-center justify-content-center h-100">
				<Col className="col-auto">
					<OverlayTrigger placement="top" overlay={<Tooltip>{message}</Tooltip>}>
						<i style={{ ...circleStyle }} />
					</OverlayTrigger>
				</Col>
			</Row>
		);
	};

	const ChecklistCellRenderer = useCallback((params) => {
		//console.log('params', params.type, params);
		if (params.type === 'invoicePayment') {
			return makeSemaphores(params.data.invoicePayment?.checkliststatus);
		} else {
			return makeSemaphores(params.value);
		}
	}, []);

	const handleOptionsOwnerships = () => {
		if (wpId && streams.length) {
			const tempStream = streams.filter((streams) => streams.streamId === wpId).pop();
			const tempTask = tempStream?.tasks.find((task) => task.id === taskId);
			//console.log('tempStream', tempStream);
			//console.log('tempTask', tempTask);

			if (!tempTask || !tempTask.responsibles) return [];

			return tempTask.responsibles.reduce((acc, obj) => {
				const objUsers = obj.users ?? [];
				const optionsArray = objUsers.map((user) => ({
					value: user.email,
					label: `${user.firstName} ${user.lastName} (${user.email})`,
				}));

				return acc.concat(optionsArray);
			}, []);
		} else {
			return [];
		}
	};

	useEffect(() => {
		fetchSubtasks();
		fetchTaskInvoices();
	}, [fetchSubtasks, fetchTaskInvoices]);

	const onGridReady = useCallback((params, gridRef) => {
		params.api.sizeColumnsToFit();

		window.addEventListener('resize', function () {
			setTimeout(function () {
				params.api.sizeColumnsToFit();
			});
		});

		//gridRef.current.api.sizeColumnsToFit();

		if (window.screen.width >= 1600) {
			//&& window.screen.height >= 768
			// Resolution is 1600 //x768 or above
			gridRef.current.api.sizeColumnsToFit();
		} else {
			const allColumnIds = [];
			gridRef.current.columnApi.getAllColumns().forEach((column) => {
				allColumnIds.push(column.getId());
			});
			gridRef.current.columnApi.autoSizeColumns(allColumnIds, true);
		}
	}, []);

	const handleCloseModal = (setShowModal) => {
		setSbShow(<></>);
		setShowModal(false);
	};

	const handleShowModal = (setShowModal) => setShowModal(true);

	const handleStatusBar = (status, message, timeout) => {
		setSbShow(<StatusBar status={status} message={message} timeout={timeout} />);
	};

	const onChange = (e, fieldName, index, list) => {
		//console.log('e:', e);
		let tempList = [...list];
		let value;

		switch (fieldName) {
			case 'code':
			case 'name':
			case 'type':
			case 'objective':
				value = e.target.value;
				break;
			case 'startDate':
			case 'endDate':
				value = e.target.value ? dateFormatterInput(e.target.value) : null;
				break;
			case 'owner':
			case 'status':
				value = e ? e.value : '';
				break;
			default:
				break;
		}

		tempList[index] = {
			...tempList[index],
			[fieldName]: value,
			position: index + 1,
		};

		setSubtasks(tempList);
	};

	const handleItemList = async (entityType, originItemList, itemList, deleteAPI, editAPI, createAPI, entityId) => {
		let resultOfActions = [];
		let messageResult = [];

		const oldItemsDeleted = originItemList.filter((object1) => {
			return !itemList.some((object2) => {
				return object1.id === object2.id;
			});
		});

		if (oldItemsDeleted.length > 0) {
			oldItemsDeleted.forEach(async (item) => {
				const deleteResult = await deleteAPI(entityId, projectWpId, taskId, item.id);
				resultOfActions.push(deleteResult.success);
			});
		}

		let validationFields = [];

		switch (entityType) {
			case 'subtasks':
				validationFields = ['name', 'startDate', 'status'];
				break;
			default:
				validationFields = [];
				break;
		}

		let result;

		await Promise.all(
			itemList.map(async (item) => {
				const resultOfEvery = validationFields.every((field) => item[field]);

				if (resultOfEvery) {
					if (item.id) {
						const { createdAt, createdBy, updatedAt, updatedBy, ...restItem } = item;
						result = await editAPI(entityId, projectWpId, taskId, restItem);
						//console.log('resultEdit:', result);
						if (result.success === false) messageResult = result;
						resultOfActions.push(result.success);
					} else {
						result = await createAPI(entityId, projectWpId, taskId, item);
						//console.log('resultNew', result);
						if (result.success === false) messageResult = result;
						resultOfActions.push(result.success);
					}
				} else {
					messageResult = {
						success: false,
						data: { validation: ['Subtask non salvati. Riempi tutti i campi obbligatori'] },
					};
					resultOfActions.push(false);
					return;
				}
			}),
		);

		//console.log('resultOfActions:', resultOfActions);
		//console.log('messageResult:', messageResult);
		return { resultOfActions, messageResult };
	};

	const handleSave = async (e) => {
		e?.preventDefault();

		setSbShow(<></>);
		setIsLoading(true);

		//let subtasksCleaned = subtasks.filter((subtask) => subtask.name && subtask.startDate && subtask.status);

		if (subtasks.length > 0) {
			const set = new Set();
			const isNotNameSubtasksUnique = subtasks.some((object) => set.size === (set.add(object.code), set.size));

			if (isNotNameSubtasksUnique) {
				handleStatusBar('error', 'Gli ID non possono essere uguali', false);
				setIsLoading(false);
				return;
			}
		}

		let resultOfActions = [true];
		let resultOfMessages = [];

		let [subtasksResults] = await Promise.all([
			handleItemList('subtasks', originSubtasks, subtasks, deleteSubtask, editSubtask, createSubtasks, projectId),
		]);
		//console.log('subtasksResults', subtasksResults);

		const resultOfAll = resultOfActions.concat(subtasksResults ? subtasksResults.resultOfActions : []);
		const resultOfAllMessages = resultOfMessages.concat(subtasksResults ? subtasksResults.messageResult : []);
		//console.log('resultOfAll', resultOfAll);
		//console.log('resultOfAllMessages', resultOfAllMessages);

		if (resultOfAll.length === 0 || (resultOfAll.length > 0 && resultOfAll.every((el) => el === true))) {
			fetchSubtasks(true);

			handleStatusBar('success', translate('common.message.edited', 'Elemento modificato con successo'), true);

			setIsLoading(false);

			setTimeout(() => {
				handleCloseModal(setShowModalEdit);
				setRefresh(true);
			}, 2000);
		} else {
			handleStatusBar(
				'error',
				resultOfAllMessages.length > 0
					? Object.values(resultOfAllMessages[0].data).join()
					: translate('common.error.save', 'Si è verificato un errore durante il salvataggio delle informazioni'),
				false,
			);

			setIsLoading(false);
		}
	};

	const addTooltip = (props) => (
		<Tooltip {...props}>
			<OETrans code="common.tooltip.addSubtask" fallback={'Aggiungi Subtask'} />
		</Tooltip>
	);
	const linkTooltip = (props) => (
		<Tooltip {...props}>
			<OETrans code="common.tooltip.linkedInvoices" fallback={'Fatture collegate'} />
		</Tooltip>
	);

	const subtaskRows = (items, setItems) => {
		const isUsed = false; //TODO: AGGIUNGERE EVENTUALE CHECK;
		let rows = [];

		const header = ['ID Subtask', 'Nome Subtask', 'Data Prevista', 'Data effettiva', 'Status', 'Ownership', 'Tipologia', 'Descrizione'];

		const addElement = (typeArray, setTypeArray) => {
			let tempTypeArray = [...typeArray];

			tempTypeArray.push({
				code: '',
				name: '',
				startDate: null,
				endDate: null,
				status: '',
				owner: '',
				type: '',
				objective: '',
				position: items.length,
			});

			setTypeArray(tempTypeArray);
		};

		const removeElement = (typeArray, setTypeArray, index) => {
			let tempTypeArray = [...typeArray];

			tempTypeArray.splice(index, 1);

			setTypeArray(tempTypeArray);
		};

		if (items) {
			if (items.length === 0) {
				items = [
					{
						code: '',
						name: '',
						startDate: null,
						endDate: null,
						status: '',
						owner: '',
						type: '',
						objective: '',
						position: 1,
					},
				];
			}

			const lastIndex = items.length - 1;

			rows = items
				.sort((valueA, valueB) => valueA.position - valueB.position)
				.map((el, i) => {
					const isThisRowFilled = el.name && el.startDate && el.status ? true : false;

					return (
						<Fragment key={'item-' + i}>
							<tr className="align-top">
								<td style={{ minWidth: '5.5rem', maxWidth: '10rem' }}>
									<Form.Control
										type="text"
										name="code"
										value={el.code ?? ''}
										onChange={(e) => onChange(e, 'code', i, items)}
										disabled={el.id || !canEdit}
									/>
								</td>
								<td style={{ maxWidth: '10rem' }}>
									<Form.Control 
										type="text" 
										name="name" 
										value={el.name ?? ''} 
										onChange={(e) => onChange(e, 'name', i, items)} 
										disabled={!canEdit}
									/>
								</td>
								<td style={{ width: '7rem' }}>
									<Form.Control
										type="date"
										name="startDate"
										value={dateFormatterInput(el.startDate) ?? ''}
										min={dateFormatterInput(wpStartDate)}
										max={dateFormatterInput(wpEndDate)}
										onChange={(e) => onChange(e, 'startDate', i, items)}
										disabled={!canEdit}
									/>
								</td>
								<td style={{ width: '7rem' }}>
									<Form.Control
										type="date"
										name="endDate"
										value={dateFormatterInput(el.endDate) ?? ''}
										min={dateFormatterInput(wpStartDate)}
										max={dateFormatterInput(wpEndDate)}
										onChange={(e) => onChange(e, 'endDate', i, items)}
										disabled={!canEdit}
									/>
								</td>
								<td style={{ minWidth: '12rem' }}>
									<Select
										components={{
											IndicatorSeparator: () => null,
										}}
										className="react-select-container"
										classNamePrefix="react-select"
										name="status"
										placeholder={translate('common.placeholder.select', 'Seleziona...')}
										value={optionsStatus.filter((opt) => opt.value === el.status)}
										options={optionsStatus}
										onChange={(e) => onChange(e, 'status', i, items)}
										isClearable
										isDisabled={!canEdit}
									/>
								</td>
								<td style={{ maxWidth: '15rem' }}>
									<Select
										components={{
											IndicatorSeparator: () => null,
										}}
										className="react-select-container"
										classNamePrefix="react-select"
										name="owner"
										placeholder={translate('common.placeholder.select', 'Seleziona...')}
										options={handleOptionsOwnerships()}
										value={handleOptionsOwnerships()?.filter((opt) => opt.value === el.owner)}
										onChange={(e) => onChange(e, 'owner', i, items)}
										isClearable
										isDisabled={!canEdit}
									/>
								</td>
								<td>
									<Form.Control 
										type="text" 
										name="type" 
										value={el.type ?? ''} 
										onChange={(e) => onChange(e, 'type', i, items)} 
										disabled={!canEdit}
									/>
								</td>
								<td>
									<Form.Control
										type="text"
										name="objective"
										value={el.objective ?? ''}
										onChange={(e) => onChange(e, 'objective', i, items)}
										disabled={!canEdit}
									/>
								</td>
								{canEdit ? (
									<td style={{ minWidth: '5rem' }}>
										{!isUsed && i === lastIndex ? (
											<Button variant="link" className="mt-1 me-2" onClick={() => addElement(items, setItems)} disabled={!isThisRowFilled}>
												<IcoPlus className="second-color ico-small indicators" />
											</Button>
										) : null}
										<Button variant="link" className="mt-1" onClick={() => removeElement(items, setItems, i)}>
											<IcoMinusCircled className="red-color ico-small indicators" />
										</Button>
									</td>
								) : null}
							</tr>
						</Fragment>
					);
				});
		}

		return (
			<>
				<table style={{ width: '100%', borderSpacing: '10px', borderCollapse: 'separate' }}>
					<thead>
						<tr>
							{header.map((title, i) => {
								const isRequired = i === 0 || i === 1 || i === 2 || i === 4 ? true : false;

								return (
									<th key={'wp-' + i} className="p-2">
										{title}
										{isRequired && <span className="required-field">*</span>}
									</th>
								);
							})}
							<th>&nbsp;</th>
						</tr>
					</thead>
					<tbody>{rows}</tbody>
				</table>
			</>
		);
	};

	const linkedInvoicesRows = () => {
		const localeText = AG_GRID_LOCALE_IT;

		const defaultColDef = {
			resizable: true,
			// wrapText: true,
			// autoHeight: true,
			//flex: 1,
			suppressMovable: true,
			cellStyle: { padding: '3px 1rem' },
			//suppressSizeToFit: false,
			//suppressColumnVirtualisation=true
		};

		const CustomTooltipRenderer = (params) => {
			//console.log('params:', params);

			let message = '';

			if (params.type === 'invoices') {
				message = 'Fatture';
			} else if (params.type === 'invoicePayment') {
				message = 'Pagamenti';
			}

			return (
				<div className="tooltipInfoBox" style={{ width: 'auto' }}>
					<span>{message}</span>
				</div>
			);
		};

		const invoicesColumn = [
			...(projectStreamConfigurationType === 'WP' && isCoordinator
				? [
						{
							headerName: 'Beneficiario',
							field: 'companyOwner.name',
							filter: 'agTextColumnFilter',
						},
					]
				: []),
			{
				headerName: 'Nr. Fattura',
				field: 'number',
				filter: 'agTextColumnFilter',
			},
			{
				headerName: 'ID Fattura',
				field: 'rpCode',
				filter: 'agTextColumnFilter',
				minWidth: 150,
			},
			{
				headerName: 'Data',
				field: 'date',
				cellRenderer: (params) => {
					return dateFormatter(params.value);
				},
				//filter: 'agTextColumnFilter',
				...columnCentered(),
				maxWidth: 150,
			},

			{
				headerName: 'Codice Fornitore',
				field: 'supplierCode',
				filter: 'agTextColumnFilter',
			},
			{
				headerName: 'Nome fornitore',
				field: 'supplierName',
				filter: 'agTextColumnFilter',
			},
			{
				headerName: 'Totale imponibile €',
				field: 'taxableAmount',
				valueGetter: (params) => {
					return formatToDecimals(params.data.taxableAmount);
				},
				filter: 'agTextColumnFilter',
				type: 'rightAligned',
			},
			{
				headerName: 'F',
				field: 'checkliststatus',
				cellRenderer: ChecklistCellRenderer,
				maxWidth: 70,
				tooltipComponent: CustomTooltipRenderer,
				tooltipField: 'checkliststatus', //obbligatorio per custom tooltip
				tooltipComponentParams: { type: 'invoices' },
				...columnCentered('', { textAlign: 'center', alignItems: 'center' }),
			},
			{
				headerName: 'P',
				field: 'invoicePayment',
				cellRenderer: ChecklistCellRenderer,
				cellRendererParams: { type: 'invoicePayment' },
				maxWidth: 70,
				tooltipComponent: CustomTooltipRenderer,
				tooltipField: 'checkliststatus', //obbligatorio per custom tooltip
				tooltipComponentParams: { type: 'invoicePayment' },
				...columnCentered('', { textAlign: 'center', alignItems: 'center' }),
			},
			{
				headerName: '',
				minWidth: 70,
				maxWidth: 70,
				cellStyle: { textAlign: 'center', padding: 0 },
				cellRenderer: userAccess.isAboveGuest ? ProjectInvoiceModal : InvoiceDetailSummaryModal,
				cellRendererParams: {
					setRefresh: setRefresh,
					projectStreamConfigurationType: projectStreamConfigurationType,
					projectAdvancedConfig: projectAdvancedConfig,
					...(!userAccess.isAboveGuest && {
						isRenderTask: true,
						setModalIsVisible: setShow,
						modalIsVisible: show,
					}),
				},
			},
		];

		return (
			<>
				<Row>
					<Col className="col-12 col-xxl-9" id="grid-wrapper">
						{Array.isArray(taskInvoices) ? (
							<div className="ag-theme-alpine list-table">
								<AgGridReact
									ref={gridRef}
									columnDefs={invoicesColumn}
									rowData={taskInvoices}
									defaultColDef={defaultColDef}
									domLayout="autoHeight"
									tooltipShowDelay={0}
									tooltipHideDelay={2000}
									tooltipMouseTrack={true}
									onGridReady={(e) => onGridReady(e, gridRef)}
									headerHeight={50}
									rowHeight={50}
									ariaDescription="ag-grid-table"
									suppressCellFocus={true}
									animateRows={true}
									localeText={localeText}
								/>
								{Object.keys(taskInvoicesMeta).length > 0 && (
									<CustomPaginationMui
										dataMeta={taskInvoicesMeta}
										setData={setTaskInvoices}
										setDataMeta={setTaskInvoicesMeta}
										refTable={'invoices'}
									/>
								)}
							</div>
						) : (
							<div className="text-center my-4">
								<Spinner animation="border" role="status">
									<span style={{ visibility: 'hidden' }}>
										<OETrans code="common.loading" fallback={'Caricamento'} />
										...
									</span>
								</Spinner>
							</div>
						)}
					</Col>
				</Row>
			</>
		);
	};

	return (
		<div>
			<OverlayTrigger placement="top" overlay={addTooltip}>
				<Button variant="link" className="me-3" onClick={() => handleShowModal(setShowModalEdit)}>
					<IcoEdit className="second-color ico-small edit" />
				</Button>
			</OverlayTrigger>
			<OverlayTrigger placement="top" overlay={linkTooltip}>
				<Button variant="link" className="me-0" onClick={() => handleShowModal(setShowModalLink)}>
					<IcoLink className="second-color ico-small cancel color-g-stroke" />
				</Button>
			</OverlayTrigger>

			{showModalEdit && (
				<Modal show={showModalEdit} size="xl" dialogClassName="modal-90w" onHide={() => handleCloseModal(setShowModalEdit)} centered>
					<Modal.Header closeButton>
						<Modal.Title>
							<OETrans code="common.add" fallback={'Aggiungi'} />
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<Form onSubmit={handleSave} id="form-subtask">
							{subtaskRows(subtasks, setSubtasks)}
						</Form>

						{canEdit && (<>
							<Row className="mt-5 mb-2">
								<span className="mx-2">
									<span className="required-field">*</span>&nbsp;
									<OETrans code="common.requiredFields" fallback={'Campi obbligatori'} />
								</span>
							</Row>

							{sbShow}
						</>)}
					</Modal.Body>

					{canEdit && (
						<Modal.Footer>
							<Button variant="primary" onClick={(e) => handleSave(e)} disabled={isLoading}>
								<OETrans code="common.add" fallback={'Aggiungi'} />
							</Button>
						</Modal.Footer>
					)}
				</Modal>
			)}

			{showModalLink && (
				<Modal show={showModalLink} size="xl" dialogClassName="modal-90w" onHide={() => handleCloseModal(setShowModalLink)} centered>
					<Modal.Header closeButton>
						<Modal.Title>
							<OETrans code="common.linkedinvoices" fallback={'Fatture collegate'} />
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>{linkedInvoicesRows()}</Modal.Body>

					<Modal.Footer />
				</Modal>
			)}
		</div>
	);
};
export default ActionBtnRendererTasks;
